import { Form, Formik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/GeneralComponents/Button";
import DomeCareTitle from "../../components/GeneralComponents/DomeCareTitle";
import ModalPhoneNumberInput from "../../components/GeneralComponents/ModalPhoneNumberInput";
import VerifiyNumberPopup from "../../components/Popups/VerifiyNumberPopup";
import history from "../../hooks/History";
import {
  forgetPasswordCheckNumber,
  getRegisterUserOTPInfo,
  setRegistrationStep,
} from "../../redux/slices/authSlice";
import { RootState } from "../../redux/store";
import { ForgetPasswordFormValues } from "../../types/api";
import { useCountriesQuery } from "../../utils/api/General/useCountries";
import { useForgetPasswordSendCode } from "../../utils/api/General/useForgetNumberSendCode";
import {
  BASE_LOCAL_IMAGES_DIR,
  DefaultNavigateDelay,
} from "../../utils/constants/CommonConstants";
import ForgetPasswordSchema from "../../utils/validations/auth/forgetPasswordSchema";
import { forget_password_IMG } from "../../utils/constants/Images";

interface ForgetPasswordProps {
  OriginType: string;
}
const ForgetPassword: React.FC<ForgetPasswordProps> = ({ OriginType }) => {
  const { t } = useTranslation("translation");
  const forgetPasswordSchema = ForgetPasswordSchema();
  const dispatch = useDispatch();
  const { phoneNumber, countryId, countryData } = useSelector(
    (state: RootState) => state.auth,
  );
  const { ProfileUserInfo } = useSelector((state: RootState) => state.profile);
  //?===============States=====================
  const [isRegexError, setIsRegexError] = useState<boolean>(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState<boolean>(false);
  const initialValues: ForgetPasswordFormValues = {
    phoneNumber:
      OriginType === "account" ? (ProfileUserInfo?.phoneNumber ?? "") : "",
    username: "",
  };

  //?====================Forget Password Form Submit====================

  const handleSubmit = (values: ForgetPasswordFormValues) => {
    if (!isRegexError) {
      setShowConfirmPopup(true);
      dispatch(
        forgetPasswordCheckNumber({
          phoneNumber: values.phoneNumber,
          countryId: countryId,
        }),
      );
    }
  };
  //?====================GET Countries Data====================

  const { data: CountriesData } = useCountriesQuery();

  //?====================POST Forget Password Query====================

  const {
    error: forgetPasswordSendCodeError,
    status: ForgetPasswordSendCodeStatus,
    mutate: ForgetPasswordSendCodeMutate,
    reset: ForgetPasswordSendCodeReset,
  } = useForgetPasswordSendCode();

  //?====================Submit Popup Function====================
  const handleNavigateToOtp = async () => {
    switch (OriginType) {
      case "account":
        if (countryId && phoneNumber) {
          ForgetPasswordSendCodeMutate(
            {
              CountryId: countryId,
              PhoneNumber: phoneNumber,
            },
            {
              onSuccess: (response) => {
                dispatch(setRegistrationStep("otp"));
                dispatch(
                  getRegisterUserOTPInfo({
                    userId: response.Data.Id,
                    otpDuration: response?.Data.Duration,
                    otpType: "forgotPassword",
                  }),
                );
                history.push("/account/forget-password/otp");
              },
            },
          );
        }
        break;
      case "auth":
        if (countryId && phoneNumber) {
          ForgetPasswordSendCodeMutate(
            {
              CountryId: countryId,
              PhoneNumber: phoneNumber,
            },
            {
              onSuccess: (response) => {
                dispatch(setRegistrationStep("otp"));
                dispatch(
                  getRegisterUserOTPInfo({
                    userId: response.Data.Id,
                    otpDuration: response?.Data.Duration,
                    otpType: "forgotPassword",
                  }),
                );
                setTimeout(() => {
                  history.push("/auth/otp");
                }, DefaultNavigateDelay);
              },
            },
          );
        }
        break;
      default:
        break;
    }
  };

  //?=====================Leave Popup================================

  return (
    <>
      <article className="z-20 flex w-full flex-col gap-6 bg-white px-8 py-10 xl:w-[75%]">
        <div className="flex flex-col gap-6">
          {/* <DomeCareTitle /> */}
          <div className="flex flex-col gap-2">
            <p className="flex flex-col items-center justify-center gap-8 py-8 text-center text-2xl font-bold text-headingTextColor">
              <img
                src={`${BASE_LOCAL_IMAGES_DIR}/${forget_password_IMG}`}
                alt=""
                className="h-20 w-20"
              />
              {t("forget_password")} 🔑
            </p>
            {OriginType === "account" ? (
              <p className="mx-auto w-3/5 text-center text-base text-textColor">
                {t("forget_password_desc_account")} 
              </p>
            ) : (
              <p className="mx-auto w-3/5 text-center text-base text-textColor">
                {t("forget_password_desc")}
              </p>
            )}
          </div>
        </div>
        <Formik
          initialValues={initialValues}
          validationSchema={forgetPasswordSchema}
          onSubmit={handleSubmit}
        >
          <Form className="flex flex-col gap-5">
            <div className="flex flex-col gap-5">
              <ModalPhoneNumberInput
                name="phoneNumber"
                placeholder={t("phone_number")}
                countriesList={CountriesData?.Data}
                getRegexError={(isRegexError) => {
                  setIsRegexError(isRegexError);
                }}
                defaultValue={ProfileUserInfo?.phoneNumber}
                autoFocus={true}
                required={true}
                disabled={OriginType === "account"}
                hideClearButton={true}
              />
            </div>
            <Button
              title={
                ForgetPasswordSendCodeStatus === "pending"
                  ? t("loading")
                  : t("send_code")
              }
              type="submit"
              variant="primary"
              extraClassName="shadow-cta"
              disabled={ForgetPasswordSendCodeStatus === "pending"}
            />
          </Form>
        </Formik>
      </article>
      {showConfirmPopup ? (
        <VerifiyNumberPopup
          setShow={() => {
            setShowConfirmPopup(false);
            ForgetPasswordSendCodeReset();
          }}
          show={showConfirmPopup}
          countryZipCode={countryData?.ZipCode}
          phoneNumber={phoneNumber}
          error={forgetPasswordSendCodeError}
          navigateAfterSuccessFunction={() => {
            handleNavigateToOtp();
          }}
          hideEditButton={OriginType === "account" ? true : false}
          editButtonOnClickFunction={() => {
            setShowConfirmPopup(false);
            ForgetPasswordSendCodeReset();
          }}
          disableButtonCondition={ForgetPasswordSendCodeStatus === "pending"}
        />
      ) : null}
    </>
  );
};

export default ForgetPassword;
